import { makeContextModule } from '@cian/react-utils';

import { type FC, type JSX, useMemo } from 'react';

import { type InitialAdfoxAnalyticsPayload, type PParams } from 'shared/types/analytics';
import { type TAdfoxBannerCompleteSizes, type TAdfoxBannerSizeProps } from 'shared/types/adfox';
import { getCompleteSizes } from 'shared/utils/getCompleteSizes';
import { type IEventEmitter } from 'shared/services/eventEmitter';

export type TTgbNativedescGeneralDesign =
  | `desktop-${'listing' | 'listingJK' | 'card'}`
  | `mobile-${'listing' | 'card'}`;

export type AdfoxBannerContext = {
  containerId: string;
  params: Record<string, string> & PParams;
  analyticsData: InitialAdfoxAnalyticsPayload;
  sizes: TAdfoxBannerCompleteSizes;
  regionId: number;
  eventEmitter: IEventEmitter;
  tgbNativedescGeneralDesign: TTgbNativedescGeneralDesign | null;
  placeId: string;
};

const {
  Context: { Consumer: AdfoxBannerContextConsumer, Provider },
  useContext: useAdfoxBannerContext,
  withContext: withAdfoxBannerContext,
} = makeContextModule<AdfoxBannerContext>({
  contextName: 'AdfoxBannerContext',
});

export { useAdfoxBannerContext, AdfoxBannerContextConsumer, withAdfoxBannerContext };

export type AdfoxBannerContextProviderProps = Omit<
  AdfoxBannerContext,
  'params' | 'sizes' | 'eventEmitter' | 'tgbNativedescGeneralDesign' | 'containerId'
> & {
  eventEmitter: IEventEmitter;
  children: JSX.Element;
  pParams: PParams;
  puids: Record<string, string>;
  sizes: TAdfoxBannerSizeProps;
  tgbNativedescGeneralDesign?: TTgbNativedescGeneralDesign | null;
  placeId: string;
};

const CONTAINER_ID_PREFIX = 'adfox';

export const AdfoxBannerContextProvider: FC<AdfoxBannerContextProviderProps> = ({
  children,
  pParams,
  puids,
  analyticsData,
  sizes,
  regionId,
  tgbNativedescGeneralDesign,
  eventEmitter,
  placeId,
}) => {
  const contextValue = useMemo<AdfoxBannerContext>(
    (): AdfoxBannerContext => ({
      analyticsData,
      get containerId(): string {
        /**
         * В режиме карусели все слоты делят один placeId. Если не дополнять id galleryPosition,
         * все баннеры получают одинаковый DOM-id: в DOM появляются дубликаты `<div id="adfox_...">`,
         * `Ya.adfoxCode.create` резолвит id в первый (уже видимый) контейнер, из-за чего
         * per-container lazyLoad перестаёт работать и грузятся сразу все баннеры карусели.
         * Для одиночного баннера galleryPosition === null — формат id не меняется.
         */
        const { galleryPosition } = this.analyticsData;

        const containerId = `${CONTAINER_ID_PREFIX}_${this.placeId}`;

        return galleryPosition == null ? containerId : `${containerId}__${galleryPosition}`;
      },
      eventEmitter,
      params: { ...puids, ...pParams },
      placeId,
      regionId,
      sizes: getCompleteSizes(sizes),
      tgbNativedescGeneralDesign: tgbNativedescGeneralDesign ?? null,
    }),
    [analyticsData, pParams, puids, sizes, regionId, tgbNativedescGeneralDesign, eventEmitter, placeId],
  );

  return <Provider value={contextValue}>{children}</Provider>;
};

AdfoxBannerContextProvider.displayName = 'AdfoxBannerContextProvider';
